import { getHttpClient } from '@/services/httpClientProvider';
import { useAuthStore } from '@/stores/auth';
import type {
  PartnerUser,
  PartnerUserDisplay,
  Organization,
  OrganizationDisplay,
  UsersListResponse,
  OrganizationsListResponse,
  FetchUsersParams,
  FetchOrganizationsParams,
  CreateOrganizationPayload,
} from './types/organizations';

export function useOrganizationsAPI() {
  const authStore = useAuthStore();
  const httpClient = getHttpClient(authStore);

  return {
    async fetchUsers(params: FetchUsersParams = {}): Promise<{ results: PartnerUser[]; totalCount: number }> {
      try {
        const {
          page = 1,
          start = 0,
          limit = 25,
          sort = [{ property: 'email', direction: 'ASC' as const }],
          orgId,
          query,
        } = params;

        const filter = params.filter ?? [
          { property: 'object', value: 'partners' },
          { property: 'object_id', value: orgId || '_no_filter_' },
          { property: 'project_partner_users', value: '_no_filter_' },
          { property: 'role_id', value: '_no_filter_' },
        ];

        const queryParams: Record<string, string> = {
          sAction: 'listing',
          page: page.toString(),
          start: start.toString(),
          limit: limit.toString(),
          sort: JSON.stringify(sort),
          filter: JSON.stringify(filter),
        };

        if (query?.trim()) {
          queryParams.query = query.trim();
        }

        const data = await httpClient.get<UsersListResponse>('admin/v4/users/', queryParams);

        if (data.success && data.users?.results) {
          return { results: data.users.results, totalCount: data.users.totalCount || 0 };
        }

        console.error('❌ OrganizationsAPI: Failed to fetch users', data);
        throw new Error(data.message || 'Failed to fetch users');
      } catch (error) {
        console.error('❌ OrganizationsAPI: Error fetching users:', error);
        throw error;
      }
    },

    async fetchOrganizations(params: FetchOrganizationsParams = {}): Promise<{ results: Organization[]; totalCount: number }> {
      try {
        const {
          page = 1,
          start = 0,
          limit = 25,
          sort = [{ property: 'name', direction: 'ASC' as const }],
          filter = [
            { property: 'id', value: '_no_filter_' },
          ],
          query,
        } = params;

        const queryParams: Record<string, string> = {
          sAction: 'listing',
          page: page.toString(),
          start: start.toString(),
          limit: limit.toString(),
          sort: JSON.stringify(sort),
          filter: JSON.stringify(filter),
        };

        if (query?.trim()) {
          queryParams.query = query.trim();
        }

        const data = await httpClient.get<OrganizationsListResponse>('admin/v4/partners/', queryParams);

        if (data.success && data.partners?.results) {
          return { results: data.partners.results, totalCount: data.partners.totalCount || 0 };
        }

        console.error('❌ OrganizationsAPI: Failed to fetch organizations', data);
        throw new Error(data.message || 'Failed to fetch organizations');
      } catch (error) {
        console.error('❌ OrganizationsAPI: Error fetching organizations:', error);
        throw error;
      }
    },

    transformUserToDisplay(user: PartnerUser, orgMap?: Map<string, Organization>): PartnerUserDisplay {
      const org = orgMap?.get(user.object_id);
      const avatarUrl = user.avatar?.trim() ? user.url_avatar : undefined;

      return {
        id: user.id,
        name: [user.first_name, user.last_name].filter(Boolean).join(' ') || 'Unknown User',
        email: user.email || '',
        orgId: user.object_id,
        orgName: org?.name,
        orgAvatarUrl: org?.avatar?.trim() ? org.url_avatar : undefined,
        role: user.c__d_role || user.c__role_label || String(user.role),
        status: user.c__d_status || (user.status === 1 ? 'Active' : 'Inactive'),
        statusRaw: user.status,
        lastLogin: user.last_login || null,
        avatarUrl,
        _uiAccess: user._uiAccess,
      };
    },

    async createOrganization(payload: CreateOrganizationPayload): Promise<Organization> {
      try {
        // API expects the same shape as an update — use a phantom id to signal new record
        const partnerRecord = {
          id: 'partners.Partners-1',
          name: payload.name,
          name_legal: payload.name_legal ?? payload.name,
          nickname: payload.nickname ?? '',
          description: payload.description ?? '',
          street_1: payload.street_1 ?? '',
          street_2: payload.street_2 ?? '',
          city: payload.city ?? '',
          state_id: payload.state_id ?? '',
          zipcode: payload.zipcode ?? '',
          country_id: payload.country_id ?? '223',
          latitude: payload.latitude ?? '',
          longitude: '',
          email: payload.email ?? '',
          phone: payload.phone ?? '',
          website: payload.website ?? '',
          status: String(payload.status ?? 1),
          package_id: payload.package_id ?? '',
          subdomain: payload.subdomain ?? '',
          email_domains: payload.email_domains ?? '',
          custom_email_domain: payload.custom_email_domain ?? '',
          ml_isolation: payload.ml_isolation ?? false,
          dashboard_ids: payload.dashboard_ids ?? '',
          // Required default fields the API expects on every put
          chart_paths: '',
          created: '',
          updated: '',
          state: '',
          country: '',
          password_policy: 1,
          private_scope: false,
          default_partners_teams_id: '',
          api_token: '',
          api_token_sandbox: true,
          m__diagnostic_report_email: '',
          m__monitoring_reports_enabled: false,
          m__preamble_text: '',
          m__preamble_url: '',
          m__work_order_form_url: '',
          m__send_advice_emails: false,
          billing_interval: 'weekly',
          billing_day: 0,
          strict_ip_validation: true,
          ip_whitelist: '0.0.0.0/0',
          _canWrite: true,
          _uiAccess: { edit: true, merge: false, clone: false, delete: true },
          _source: '',
          _merge: '',
          merge_ids: '',
          c__d_status: payload.status === 2 ? 'Inactive' : 'Active',
          c__d_state_id: '',
          c__d_country_id: '',
          project_partner_ids_ext: [null],
        };

        const body = new URLSearchParams();
        body.append('context_org_id', '');
        body.append('partners', JSON.stringify([partnerRecord]));

        const data = await httpClient.post<OrganizationsListResponse>(
          'admin/v4/partners/',
          body,
          { sAction: 'put' },
        );

        if (data.success && data.partners?.results?.[0]) {
          return data.partners.results[0];
        }

        console.error('❌ OrganizationsAPI: Failed to create organization', data);
        throw new Error(data.message || 'Failed to create organization');
      } catch (error) {
        console.error('❌ OrganizationsAPI: Error creating organization:', error);
        throw error;
      }
    },

    transformOrgToDisplay(org: Organization): OrganizationDisplay {
      const typeLabels: Record<string, string> = {
        standard: 'Standard',
        service_provider: 'Service Provider',
        reseller: 'Reseller',
        ecosystem: 'Ecosystem',
      };

      const primaryType = org.types?.[0] ?? 'standard';
      const avatarUrl = org.avatar?.trim() ? org.url_avatar : undefined;

      return {
        id: org.id,
        name: org.name,
        type: typeLabels[primaryType] ?? primaryType,
        status: org.c__d_status || (org.status === 1 ? 'Active' : 'Inactive'),
        statusRaw: org.status,
        email: org.email || '',
        userCount: org.c__user_count ?? 0,
        issuesCount: org.c__issues_count ?? 0,
        avatarUrl,
        created: org.created || null,
        _uiAccess: org._uiAccess,
      };
    },
  };
}
